import { useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import logger from '@widgets/logger';
import HelpIcon from '@mui/icons-material/Help';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { Input } from '@components/Input';
import { ColorPicker } from '@components/ColorPicker';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import { Switch } from '@components/Switch';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import Tooltip from '@components/Tooltip';
import TextEditor from '@components/TextEditor';
import { surveyService } from '@services';
import { useTranslation, useCopyToClipboard, useThrottle } from '@hooks';
import { AITaskType, AI_TASK_TYPE } from '@constants/ai-assistant';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { MB } from '@constants/file';
import { CopyIcon } from 'assets/images/icons';
import { TwoColumnsForm } from './styles';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';

const FollowUpSelect = styled(Select)({
    width: '75px',
    '& .MuiFormControl-root': {
        padding: '0 !important',
    },
});

export const HalfFieldSelect = styled(Select)({
    maxWidth: 250,
    '@media (max-width: 600px)': {
        maxWidth: 'none',
    },
    '& .MuiInputBase-adornedEnd': {
        paddingRight: 0,
    },
});

const SelectSecondaryIcon = styled(IconButton)({
    marginRight: 0,
    position: 'absolute',
    left: -60,
    padding: 8,
});

const followUpOptions = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
    { value: 6, label: '6' },
    { value: 7, label: '7' },
];

const Customization = ({ isView, locations, brands, selectedSurvey }) => {
    const { t } = useTranslation();
    const [isUploading, setIsUploading] = useState(false);

    const { control, setValue, getValues, watch } = useFormContext();
    const { onCopy } = useCopyToClipboard();
    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);

    const mainLocationId = watch('mainLocationId');
    const brandId = watch('brandId');
    const locationType = watch('locationType');
    const followUpEnable = watch('followUpEnable');
    const customizeQrCodeDesign = watch('customizeQrCodeDesign');
    const qrLogoUrl = watch('qrLogoUrl');

    const locationsOptions = useMemo(() => {
        if (!brandId) return [];
        const availableLocationsIds = brands.find(b => b.id === brandId)?.locations?.map(l => l.id);
        return availableLocationsIds
            .map(id => {
                const location = locations.find(l => l.companyId === id);
                if (!location) return undefined;
                return { value: location.companyId, label: location.internalName };
            })
            .filter(Boolean);
    }, [locations, brands, brandId]);

    const protectionOptions = [
        { value: 0, label: t('common.none') },
        { value: 1, label: t('FeedbackSurveys.day') },
        { value: 2, label: t('FeedbackSurveys.days3') },
        { value: 3, label: t('FeedbackSurveys.week') },
        { value: 4, label: t('FeedbackSurveys.weeks2') },
        { value: 5, label: t('FeedbackSurveys.month') },
        { value: 6, label: t('FeedbackSurveys.months3') },
        { value: 7, label: t('FeedbackSurveys.months6') },
        { value: 8, label: t('FeedbackSurveys.year') },
    ];

    const uploadLogo = async e => {
        try {
            setIsUploading(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await surveyService.uploadQRLogoImage(data, selectedSurvey.id);
            setValue('qrLogoUrl', response.publicUrl, { shouldDirty: true });
        } catch (_) {
            logger.error("Couldn't upload logo for survey");
        }
        setIsUploading(false);
        e.target.value = '';
    };

    const onDeleteLogo = () => setValue('qrLogoUrl', null, { shouldDirty: true });

    const locationSurveyId = `${selectedSurvey?.externalId}${
        locationType === LOCATION_TYPE.BRAND ? `:${mainLocationId}` : ''
    }`;
    const qrLink = `${process.env.REACT_APP_SURVEY_DOMAIN}/q/${locationSurveyId}`;

    return (
        <>
            <Typography variant="subtitle1" sx={{ marginBottom: '24px', color: 'rgba(0,0,0,0.87)' }}>
                {t('FeedbackSurveys.messageTemplate')}
            </Typography>
            <Controller
                control={control}
                name="message"
                render={({ field, fieldState: { error }, ref }) => (
                    <TextEditor
                        {...field}
                        ref={ref}
                        label={t('FeedbackSurveys.messageTemplate')}
                        initialValue={selectedSurvey?.message || ''}
                        onChange={text =>
                            setValue('message', parseTipTapJSONToPlainText(text), {
                                shouldValidate: true,
                                shouldDirty: true,
                            })
                        }
                        editable={!isView}
                        error={error}
                        aiAssistant={{
                            additionalOptions: [
                                {
                                    text: t('AIAssistant.writeTemplate'),
                                    type: AITaskType.GenerateSurveyTemplateMessage,
                                    taskType: AI_TASK_TYPE.CONTEXTUAL,
                                    companyId: mainLocationId,
                                },
                            ],
                        }}
                    />
                )}
            />
            <Controller
                control={control}
                name="followUpEnable"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={getValues('followUpEnable')}
                        handleChange={event => {
                            setValue('followUpEnable', event.target.checked, { shouldDirty: true });
                        }}
                        label={t('FeedbackSurveys.followUpLabel')}
                        disabled={isView}
                    />
                )}
            />
            <Typography variant="caption" color="textSecondary" sx={{ marginBottom: followUpEnable ? 0 : '24px' }}>
                {t('FeedbackSurveys.followUpDescription')}
            </Typography>
            {followUpEnable && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '16px 0' }}>
                        <Typography variant="body2">{t('FeedbackSurveys.wait')}</Typography>
                        <Controller
                            control={control}
                            name="followUpDelay"
                            render={({ field, ref }) => (
                                <FollowUpSelect
                                    {...field}
                                    ref={ref}
                                    label={t('common.delay')}
                                    options={followUpOptions}
                                    disabled={isView}
                                />
                            )}
                        />
                        <Typography variant="body2">{t('FeedbackSurveys.days')}</Typography>
                    </div>
                    <Controller
                        control={control}
                        name="followUpText"
                        render={({ field, fieldState: { error }, ref }) => (
                            <TextEditor
                                {...field}
                                ref={ref}
                                label={t('FeedbackSurveys.followUpMessage')}
                                initialValue={selectedSurvey?.followUpText || ''}
                                onChange={text =>
                                    setValue('followUpText', parseTipTapJSONToPlainText(text), {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                    })
                                }
                                editable={!isView}
                                error={error}
                                aiAssistant={{
                                    additionalOptions: [
                                        {
                                            text: t('AIAssistant.writeTemplate'),
                                            type: AITaskType.GenerateSurveyTemplateFollowUp,
                                            taskType: AI_TASK_TYPE.CONTEXTUAL,
                                            companyId: mainLocationId,
                                        },
                                    ],
                                }}
                            />
                        )}
                    />
                </>
            )}
            <Controller
                control={control}
                name="overSurvey"
                render={({ field, ref }) => (
                    <HalfFieldSelect
                        {...field}
                        ref={ref}
                        label={t('FeedbackSurveys.protectionLabel')}
                        options={protectionOptions}
                        disabled={isView}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" style={{ position: 'relative' }}>
                                    <SelectSecondaryIcon>
                                        <Tooltip title={t('FeedbackSurveys.protectionTooltip')} placement="right">
                                            <HelpIcon sx={{ fontSize: '16px' }} />
                                        </Tooltip>
                                    </SelectSecondaryIcon>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            />
            <Typography variant="subtitle1" sx={{ marginBottom: '16px', color: 'rgba(0,0,0,0.87)' }}>
                {t('FeedbackSurveys.customizationQR')}
            </Typography>
            {brandId && (
                <HalfFieldSelect
                    value={mainLocationId}
                    onChange={e => setValue('mainLocationId', e.target.value, { shouldDirty: true })}
                    label={t('common.location')}
                    options={locationsOptions}
                    disabled={isView}
                    required
                    style={{ marginTop: '8px' }}
                />
            )}
            <Input
                inputProps={{ value: qrLink }}
                label={t('common.url')}
                fullWidth
                onAddonClick={() => onCopyClick(qrLink)}
                addonComponent={<CopyIcon style={{ fill: 'rgba(0, 0, 0, 0.6)' }} />}
                sx={{ marginTop: '8px' }}
                disabled
            />
            <Controller
                control={control}
                name="customizeQrCodeDesign"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => field.onChange(event.target.checked)}
                        label={t('FeedbackSurveys.customizeQrCodeDesign')}
                        sx={{ marginBottom: '8px' }}
                        disabled={isView}
                    />
                )}
            />
            {customizeQrCodeDesign ? (
                <>
                    <Typography variant="subtitle1">{t('common.logo')}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', margin: '4px 0 8px' }}>
                        {t('FeedbackSurveys.recommendedDimension')}
                    </Typography>
                    <ImageUploaderSingle
                        maxSize={10 * MB}
                        logoUrl={qrLogoUrl}
                        disabled={isUploading || isView}
                        onDelete={onDeleteLogo}
                        uploadLogo={uploadLogo}
                        style={{ marginBottom: 24 }}
                        isUploading={isUploading}
                    />
                    <Controller
                        control={control}
                        name="topText"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{
                                    ...field,
                                    onChange: e => field.onChange(e.target.value),
                                }}
                                label={t('FeedbackSurveys.topText')}
                                fullWidth
                                error={!!error}
                                sx={{ marginBottom: '8px' }}
                                helperText={error ? t(error.message, { count: 42 }) : null}
                                disabled={isView}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="bottomText"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{
                                    ...field,
                                    onChange: e => field.onChange(e.target.value),
                                }}
                                label={t('FeedbackSurveys.bottomText')}
                                fullWidth
                                error={!!error}
                                sx={{ marginBottom: '8px' }}
                                helperText={error ? t(error.message, { count: 42 }) : null}
                                disabled={isView}
                            />
                        )}
                    />
                    <Typography variant="subtitle1" sx={{ marginBottom: '8px' }}>
                        {t('FeedbackSurveys.colors')}
                    </Typography>
                    <TwoColumnsForm>
                        <Controller
                            control={control}
                            name="backgroundColor"
                            render={({ field, fieldState: { error }, ref }) => (
                                <ColorPicker
                                    ref={ref}
                                    inputProps={{
                                        ...field,
                                        onChange: e => {
                                            const val = e?.target?.value ? e.target.value : e;
                                            field.onChange(val);
                                        },
                                    }}
                                    label={t('common.background')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    disabled={isView}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="fillColor"
                            render={({ field, fieldState: { error }, ref }) => (
                                <ColorPicker
                                    ref={ref}
                                    inputProps={{
                                        ...field,
                                        onChange: e => {
                                            const val = e?.target?.value ? e.target.value : e;
                                            field.onChange(val);
                                        },
                                    }}
                                    label={t('FeedbackSurveys.fillColor')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    disabled={isView}
                                />
                            )}
                        />
                    </TwoColumnsForm>
                </>
            ) : null}
        </>
    );
};

export default Customization;
